import { createFileRoute, redirect } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { listFlags, upsertFlag, deleteFlag } from "@/lib/feature-flags.functions";
import { PageHeader } from "@/components/admin/AdminPrimitives";
import { Plus, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

/**
 * SDE.4 · Onda 4.4 — Gerência de flags é operação `admin_full`.
 * Enforcement real vive nas server functions (upsert/deleteFlag também
 * podem ser elevadas em ondas futuras). Este beforeLoad é UX + defesa em
 * profundidade e SÓ é ativado quando `rbac_enforced=true`.
 */
export const Route = createFileRoute("/_authenticated/admin/flags")({
  head: () => ({ meta: [{ title: "Feature Flags — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  beforeLoad: async () => {
    const [{ data: flag }, { data: level }] = await Promise.all([
      supabase.from("feature_flags").select("enabled").eq("key", "rbac_enforced").maybeSingle(),
      supabase.rpc("current_admin_level"),
    ]);
    if (flag?.enabled === true && level !== "full") throw redirect({ to: "/admin" });
  },
  component: FlagsPage,
});

type Flag = {
  id?: string; key: string; description?: string | null;
  enabled: boolean; rollout_pct: number; target_user_ids: string[];
  admin_only: boolean; environment: "all" | "preview" | "production";
};

function emptyFlag(): Flag {
  return { key: "", description: "", enabled: false, rollout_pct: 0, target_user_ids: [], admin_only: false, environment: "all" };
}

function FlagsPage() {
  const listFn = useServerFn(listFlags);
  const upsertFn = useServerFn(upsertFlag);
  const deleteFn = useServerFn(deleteFlag);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-flags"], queryFn: () => listFn() });
  const [editing, setEditing] = useState<Flag | null>(null);

  const save = useMutation({
    mutationFn: (f: Flag) => upsertFn({ data: {
      ...(f.id ? { id: f.id } : {}), key: f.key, description: f.description ?? null,
      enabled: f.enabled, rollout_pct: f.rollout_pct, target_user_ids: f.target_user_ids,
      admin_only: f.admin_only, environment: f.environment,
    } }),
    onSuccess: () => { toast.success("Flag salva"); qc.invalidateQueries({ queryKey: ["admin-flags"] }); setEditing(null); },
    onError: (e: any) => toast.error(e?.message ?? "Falha ao salvar"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => { toast.success("Flag removida"); qc.invalidateQueries({ queryKey: ["admin-flags"] }); },
  });
  const toggle = useMutation({
    mutationFn: (f: Flag) => upsertFn({ data: {
      ...(f.id ? { id: f.id } : {}), key: f.key, description: f.description ?? null,
      enabled: !f.enabled, rollout_pct: f.rollout_pct, target_user_ids: f.target_user_ids,
      admin_only: f.admin_only, environment: f.environment,
    } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-flags"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <PageHeader title="Feature Flags" description="Ativar, desativar e liberar funcionalidades gradualmente." />
        <button
          onClick={() => setEditing(emptyFlag())}
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--navy)] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[var(--navy)]/90"
        >
          <Plus size={12} /> Nova flag
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-black/[0.06] bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
              <th className="px-4 py-3">Chave</th><th className="px-4 py-3">Ativa</th>
              <th className="px-4 py-3">Rollout</th><th className="px-4 py-3">Escopo</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {(q.data ?? []).map((f: any) => (
              <tr key={f.id} className="border-t border-black/5">
                <td className="px-4 py-3">
                  <p className="font-mono text-xs font-bold">{f.key}</p>
                  {f.description && <p className="text-[11px] text-[var(--gray-ink)]">{f.description}</p>}
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => toggle.mutate(f)} className="inline-flex items-center gap-1 text-xs">
                    {f.enabled ? <ToggleRight className="text-[var(--green)]" size={22} /> : <ToggleLeft className="text-black/30" size={22} />}
                  </button>
                </td>
                <td className="px-4 py-3">{f.rollout_pct}%</td>
                <td className="px-4 py-3 text-xs">
                  {f.admin_only && <span className="mr-1 rounded bg-[var(--gold)]/15 px-1.5 py-0.5 text-[var(--gold)]">admin</span>}
                  {f.environment !== "all" && <span className="mr-1 rounded bg-black/5 px-1.5 py-0.5">{f.environment}</span>}
                  {(f.target_user_ids?.length ?? 0) > 0 && (
                    <span className="rounded bg-black/5 px-1.5 py-0.5">{f.target_user_ids.length} usuário(s)</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(f)} className="text-xs font-semibold text-[var(--navy)] hover:underline">Editar</button>
                  <button onClick={() => confirm("Remover flag?") && remove.mutate(f.id)} className="ml-3 inline-flex text-red-600" aria-label="Remover">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {(q.data ?? []).length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[var(--gray-ink)]">Nenhuma flag ainda.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="mb-4 font-display text-lg font-extrabold">{editing.id ? "Editar flag" : "Nova flag"}</h3>
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="text-xs font-semibold">Chave</span>
                <input
                  value={editing.key}
                  onChange={(e) => setEditing({ ...editing, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                  disabled={!!editing.id}
                  className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 font-mono text-xs"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold">Descrição</span>
                <input value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="mt-1 w-full rounded-md border border-black/10 px-3 py-2" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold">Rollout %</span>
                  <input type="number" min={0} max={100} value={editing.rollout_pct}
                    onChange={(e) => setEditing({ ...editing, rollout_pct: Math.max(0, Math.min(100, Number(e.target.value))) })}
                    className="mt-1 w-full rounded-md border border-black/10 px-3 py-2" />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold">Ambiente</span>
                  <select value={editing.environment}
                    onChange={(e) => setEditing({ ...editing, environment: e.target.value as never })}
                    className="mt-1 w-full rounded-md border border-black/10 px-3 py-2">
                    <option value="all">Todos</option>
                    <option value="preview">Preview</option>
                    <option value="production">Produção</option>
                  </select>
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.enabled}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} />
                Ativa
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.admin_only}
                  onChange={(e) => setEditing({ ...editing, admin_only: e.target.checked })} />
                Apenas administradores
              </label>
              <label className="block">
                <span className="text-xs font-semibold">Usuários-alvo (UUIDs separados por vírgula)</span>
                <input
                  value={editing.target_user_ids.join(", ")}
                  onChange={(e) => setEditing({ ...editing, target_user_ids: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                  className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 font-mono text-xs"
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="rounded-lg px-3 py-2 text-sm font-semibold hover:bg-[var(--gray-soft)]">Cancelar</button>
              <button onClick={() => save.mutate(editing)} disabled={save.isPending || !editing.key}
                className="rounded-lg bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {save.isPending ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
